<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <!-- Brand Section -->
        <div class="footer-brand">
          <div class="brand-logo">
            <span class="spring-icon">🌱</span>
            <span class="brand-text">KWIQ Spring Boot</span>
          </div>
          <p class="brand-description">
            Master Spring Boot development with interactive code samples, comprehensive tutorials, and production-ready examples.
          </p>
          <div class="footer-links">
            <a href="https://express.kwiqsolutions.com" target="_blank" class="footer-link">
              Express.js Learning Platform
            </a>
            <a href="https://pyspark.kwiqsolutions.com" target="_blank" class="footer-link">
              PySpark Learning Platform
            </a>
            <a href="https://kwiqsolutions.com" target="_blank" class="footer-link">
              KWIQ Solutions Business
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="footer-section">
          <h4>Quick Links</h4>
          <nav class="footer-nav">
            <RouterLink to="/" class="footer-nav-link">Home</RouterLink>
            <RouterLink to="/samples" class="footer-nav-link">Code Samples</RouterLink>
            <RouterLink to="/tools" class="footer-nav-link">pom.xml Generator</RouterLink>
            <RouterLink to="/about" class="footer-nav-link">About</RouterLink>
          </nav>
        </div>

        <!-- Sample Categories -->
        <div class="footer-section">
          <h4>Sample Categories</h4>
          <nav class="footer-nav">
            <RouterLink to="/samples/getting-started" class="footer-nav-link">Getting Started</RouterLink>
            <RouterLink to="/samples/web-development" class="footer-nav-link">Web Development</RouterLink>
            <RouterLink to="/samples/data-access" class="footer-nav-link">Data Access</RouterLink>
            <RouterLink to="/samples/security" class="footer-nav-link">Security</RouterLink>
            <RouterLink to="/samples/testing" class="footer-nav-link">Testing</RouterLink>
          </nav>
        </div>

        <!-- Resources -->
        <div class="footer-section">
          <h4>Resources</h4>
          <nav class="footer-nav">
            <a href="https://spring.io/projects/spring-boot" target="_blank" class="footer-nav-link">
              Spring Boot Docs
            </a>
            <a href="https://spring.io/guides" target="_blank" class="footer-nav-link">
              Spring Guides
            </a>
            <a href="https://github.com/spring-projects/spring-boot" target="_blank" class="footer-nav-link">
              GitHub
            </a>
            <a href="https://stackoverflow.com/questions/tagged/spring-boot" target="_blank" class="footer-nav-link">
              Stack Overflow
            </a>
          </nav>
        </div>
      </div>

      <!-- Footer Bottom -->
      <div class="footer-bottom">
        <div class="footer-bottom-content">
          <p class="copyright">
            © {{ currentYear }} KWIQ Solutions.
          </p>
          <div class="footer-tech">
            <span class="tech-badge">Spring Boot</span>
            <span class="tech-badge">Java</span>
            <span class="tech-badge">Maven</span>
            <span class="tech-badge">JPA</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { computed } from 'vue'

const currentYear = computed(() => new Date().getFullYear())
</script>

<style scoped>
.footer {
  background: linear-gradient(135deg, var(--surface), #f1f5f9);
  border-top: 2px solid var(--primary-color);
  margin-top: auto;
}

.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  padding: 3rem 0;
}

.footer-brand {
  max-width: 300px;
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
}

.spring-icon {
  font-size: 1.75rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.brand-text {
  color: var(--text-color);
}

.brand-description {
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.footer-link {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: var(--transition);
}

.footer-link:hover {
  color: var(--primary-color-dark);
  text-decoration: underline;
}

.footer-section h4 {
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
  display: inline-block;
}

.footer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-nav-link {
  color: var(--text-light);
  text-decoration: none;
  font-size: 0.875rem;
  transition: var(--transition);
  padding: 0.25rem 0;
}

.footer-nav-link:hover,
.router-link-active.footer-nav-link {
  color: var(--primary-color);
  transform: translateX(4px);
}

.footer-bottom {
  border-top: 1px solid var(--border);
  padding: 1.5rem 0;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  color: var(--text-light);
  font-size: 0.875rem;
  margin: 0;
}

.footer-tech {
  display: flex;
  gap: 0.5rem;
}

.tech-badge {
  background: rgba(76, 175, 80, 0.1);
  color: var(--primary-color);
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  
  .footer-brand {
    grid-column: 1 / -1;
    max-width: none;
    margin-bottom: 1rem;
  }
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  .footer-brand {
    text-align: center;
  }
  
  .footer-links {
    align-items: center;
  }
  
  .footer-bottom-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .footer-tech {
    justify-content: center;
  }
}
</style>